/**
 * MIGIS Native Bridge Protocol (MNBP/1) — frame types, error codes, and
 * helpers shared by the BridgeHost and any JS-side tooling.
 *
 * Contract: MIGIS_NATIVE_SDK_SPEC.md §2. Frames are JSON-RPC 2.0 objects,
 * one JSON object per transport message, UTF-8.
 */

export const MNBP_PROTOCOL_VERSION = 1;
export const JSONRPC = '2.0';

// ─── Error codes (spec §2.4) ─────────────────────────────────────────────
export const ERR_PARSE = -32700;
export const ERR_INVALID_REQUEST = -32600;
export const ERR_METHOD_NOT_FOUND = -32601;
export const ERR_INVALID_PARAMS = -32602;
export const ERR_HOST = -32000;
export const ERR_UNKNOWN_VIEW = -32001;
export const ERR_VIEW_KIND = -32002;
export const ERR_NOT_READY = -32003;
export const ERR_ENGINE = -32004;
export const ERR_UNSUPPORTED = -32005;

export interface BridgeRequest {
  jsonrpc: '2.0';
  id: number;
  method: string;
  params?: Record<string, unknown>;
}

export interface BridgeResponse {
  jsonrpc: '2.0';
  id: number;
  result?: unknown;
  error?: { code: number; message: string; data?: unknown };
}

export interface BridgeEventNotification {
  jsonrpc: '2.0';
  method: 'event';
  params: { view: string; type: string; data: unknown };
}

export type BridgeFrame = BridgeRequest | BridgeResponse | BridgeEventNotification;

export class BridgeMethodError extends Error {
  constructor(public code: number, message: string, public data?: unknown) {
    super(message);
    this.name = 'BridgeMethodError';
  }
}

export function makeResult(id: number, result: unknown): string {
  return JSON.stringify({ jsonrpc: JSONRPC, id, result: result ?? {} });
}

export function makeError(id: number | null, code: number, message: string, data?: unknown): string {
  const err: Record<string, unknown> = { code, message };
  if (data !== undefined) err.data = data;
  return JSON.stringify({ jsonrpc: JSONRPC, id, error: err });
}

export function makeEvent(view: string, type: string, data: unknown): string {
  return JSON.stringify({ jsonrpc: JSONRPC, method: 'event', params: { view, type, data: data ?? {} } });
}

/** Parse an incoming frame; returns a request or throws BridgeMethodError. */
export function parseRequest(raw: string): BridgeRequest {
  let obj: any;
  try {
    obj = JSON.parse(raw);
  } catch {
    throw new BridgeMethodError(ERR_PARSE, 'malformed JSON frame');
  }
  if (!obj || obj.jsonrpc !== JSONRPC || typeof obj.method !== 'string') {
    throw new BridgeMethodError(ERR_INVALID_REQUEST, 'not a JSON-RPC 2.0 request');
  }
  if (obj.id !== undefined && (typeof obj.id !== 'number' || !Number.isFinite(obj.id))) {
    throw new BridgeMethodError(ERR_INVALID_REQUEST, 'id must be a finite number');
  }
  if (obj.params !== undefined && (typeof obj.params !== 'object' || Array.isArray(obj.params))) {
    throw new BridgeMethodError(ERR_INVALID_PARAMS, 'params must be an object');
  }
  return obj as BridgeRequest;
}

/** Event types a host may emit (spec §2.6). */
export const BRIDGE_EVENTS_2D = [
  'click', 'dblclick', 'contextmenu', 'pointermove',
  'movestart', 'moveend', 'zoomchange', 'rotationchange', 'load', 'error',
] as const;
export const BRIDGE_EVENTS_3D = ['ready', 'click', 'camera-change', 'error'] as const;
export const BRIDGE_EVENTS_SESSION = ['view.created', 'view.destroyed'] as const;
export const BRIDGE_EVENTS_DRAW = ['draw.point', 'draw.create', 'draw.abort'] as const;
export const BRIDGE_EVENTS_MARKER = ['marker.click'] as const;

export const HOST_CAPABILITIES = [
  '2d', '3d', 'layers', 'markers', 'draw', 'analysis', 'export', 'auth',
] as const;
